import React from 'react';
import { action, makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import { fileService } from '@services/fileService.ts';
import { ImgGear } from '@components/Images/ImgGear.tsx';
import { ImgUser } from '@components/Images/ImgUser.tsx';
import { ImgEye } from '@components/Images/ImgEye.tsx';
import { ImgCheck } from '@components/Images/ImgCheck.tsx';

export type EditUserData = {
  displayName: string;
  email: string;
  storageLimit: number;   // bytes
  roles: string[];
  usedSpace: number;
};

export type EditUserResult = {
  displayName: string;
  email: string;
  storageLimit: number;   // bytes
  password?: string;
  roles: string[];
};

const ALL_ROLES = ['admin', 'user', 'guest'] as const;
type Role = typeof ALL_ROLES[number];

const ROLE_LABELS: Record<Role, React.ReactNode> = {
  admin: <><ImgGear /> Admin</>,
  user: <><ImgUser size={14} /> User</>,
  guest: <><ImgEye /> Guest</>,
};

@observer
export class EditUserDialog extends React.Component<EditUserData> implements IModalDialogContent<EditUserResult> {
  @observable private _displayName: string;
  @observable private _email: string;
  @observable private _storageLimitGb: string;
  @observable private _usedSpace: number;
  @observable private _password = '';
  @observable private _roles: string[];

  constructor(props: EditUserData) {
    super(props);
    this._displayName = props.displayName;
    this._email = props.email;
    this._storageLimitGb = props.storageLimit ? (props.storageLimit / (1024 ** 3)).toFixed(1) : '0';
    this._usedSpace = props.usedSpace;
    this._roles = [...props.roles];
    makeObservable(this);
  }

  getModalOptions(window: ModalWindow<EditUserResult>): ModalDialogOptions<EditUserResult> {
    return {
      title: `Edit — ${this._displayName || this._email}`,
      width: '500px',
      closeByEscape: true,
      closeOnClickOutside: false,
      buttons: [
        {
          title: 'Save',
          type: ModalButtonType.Save,
          color: 'primary',
          onClick: () => {
            const result: EditUserResult = {
              displayName: this._displayName.trim(),
              email: this._email.trim(),
              storageLimit: parseFloat(this._storageLimitGb || '0') * 1024 ** 3,
              roles: [...this._roles],
            };
            if (this._password) result.password = this._password;
            window.setPayload(result);
            window.close(ModalButtonType.Save);
          },
        },
        {
          title: 'Cancel',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel),
        },
      ],
    };
  }

  render() {
    const limitBytes = parseFloat(this._storageLimitGb || '0') * 1024 ** 3;
    const pct = limitBytes > 0 ? Math.min(100, (this._usedSpace / limitBytes) * 100) : 0;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
        {/* Basic info */}
        <div className="form-grid">
          <div className="form-row">
            <label>Display Name</label>
            <input className="form-control" value={this._displayName} onChange={this._onDisplayNameChange} autoFocus />
          </div>
          <div className="form-row">
            <label>Email</label>
            <input className="form-control" value={this._email} onChange={this._onEmailChange} />
          </div>
          <div className="form-row">
            <label>New Password</label>
            <input
              className="form-control"
              type="password"
              placeholder="Leave empty to keep current"
              value={this._password}
              onChange={this._onPasswordChange}
            />
          </div>
          <div className="form-row">
            <label>Storage Limit (GB)</label>
            <input
              className="form-control"
              type="number"
              min="0"
              step="0.5"
              value={this._storageLimitGb}
              onChange={this._onStorageLimitChange}
            />
          </div>
        </div>

        {/* Storage bar */}
        {limitBytes > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Used: {fileService.formatFileSize(this._usedSpace)}</span>
              <span>{pct.toFixed(0)}% of {fileService.formatFileSize(limitBytes)}</span>
            </div>
            <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border-color)', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: pct > 90 ? 'var(--danger-color)' : 'var(--accent-color)', transition: 'width 0.3s' }} />
            </div>
          </div>
        )}

        {/* Roles */}
        <div className="form-row" style={{ marginBottom: 0 }}>
          <label>Roles</label>
          <div className="role-tag-selector">
            {ALL_ROLES.map(role => {
              const active = this._roles.includes(role);
              return (
                <button
                  key={role}
                  type="button"
                  className={`role-tag-btn role-tag-${role}${active ? ' active' : ''}`}
                  onClick={() => this._onToggleRole(role)}
                >
                  {active && <span className="role-tag-check"><ImgCheck /></span>}
                  {ROLE_LABELS[role]}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  @action.bound private _onDisplayNameChange(e: React.ChangeEvent<HTMLInputElement>) { this._displayName = e.target.value; }
  @action.bound private _onEmailChange(e: React.ChangeEvent<HTMLInputElement>) { this._email = e.target.value; }
  @action.bound private _onPasswordChange(e: React.ChangeEvent<HTMLInputElement>) { this._password = e.target.value; }
  @action.bound private _onStorageLimitChange(e: React.ChangeEvent<HTMLInputElement>) { this._storageLimitGb = e.target.value; }

  @action.bound
  private _onToggleRole(role: string) {
    const idx = this._roles.indexOf(role);
    if (idx >= 0) {
      this._roles.splice(idx, 1);
    } else {
      this._roles.push(role);
    }
  }
}
